import React, { useEffect, useState } from "react";
import { Filter } from "react-feather";
import CommonModal from "../../../../CommonElements/Functions/CommonModels";
import TreeView from "react-accessible-treeview";
import { Card, CardBody, Col } from "reactstrap";
import CommonCardHeader from "../../../../Utils/CommonComponents/CommonCardHeader";
import { basicTreeSubTitle } from "../../../../Data/BonusUiData/TreeViewData";
import { BasicTreeTitle } from "../../../../Utils/Constants";
import {
  ArrowIcon,
  CheckBoxIcon,
} from "../../../BonusUi/TreeView/Common/ArrowIcon";
import {
  ensureDexieReady,
  getDataFromDexie,
  getDataFromDexieArray,
} from "../../../../db/DexieServises";
import { INode } from "react-accessible-treeview";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../redux-toolkit/store";
import { setPositions } from "../../../../redux-toolkit/positionSlice";

interface TreeModelProps {
  onSelectionChange?: (ids: (string | number)[]) => void;
}

export default function TreeModel({ onSelectionChange }: TreeModelProps) {
  const dispatch = useDispatch();
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [treeData, setTreeData] = useState<INode<any>[]>([]);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [rawData, setRawData] = useState<any[]>([]);
  const [employeeMap, setEmployeeMap] = useState<Record<number, string>>({});
  const unitName = useSelector((state: RootState) => state.orgUnit.name);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);

  const getDisplayName = (row: any) => {
    if (!row) return "";
    const posName = (row.PositionName || row.position_name || row.positionName || "").trim();
    const empName = (
      row.EmployeeName ||
      row.employee_name ||
      row.Employee_Name ||
      row.employeeName ||
      employeeMap[row.PositionId] ||
      ""
    ).trim();
    return [posName, empName].filter(Boolean).join(" - ") || posName || "Unnamed";
  };

  useEffect(() => {
    const loadPositionData = async () => {
      const tableName = "Position";

      try {
        await ensureDexieReady();

        const empMap: Record<number, string> = {};
        try {
          const empData = await getDataFromDexie("Employee");
          if (Array.isArray(empData)) {
            empData.forEach((emp: any) => {
              if (emp.PositionId) {
                const name = `${emp.FirstName || ""} ${emp.LastName || ""}`.trim();
                if (name) empMap[emp.PositionId] = name;
              }
            });
          }
          setEmployeeMap(empMap);
        } catch (e) {
          // ignore
        }

        var rawData = await getDataFromDexieArray("Position", "PositionId");
        const filtered = Object.values(rawData).filter(
          (item: any) => item.OrgUnitId === unitId
        );
        // console.log('✅ Filtered Data:', filtered);
        setRawData(filtered);

        const formatNodeName = (row: any) => {
          const posName = (row.PositionName || row.position_name || row.positionName || "").trim();
          const empName = (
            row.EmployeeName ||
            row.employee_name ||
            row.Employee_Name ||
            row.employeeName ||
            empMap[row.PositionId] ||
            ""
          ).trim();
          return [posName, empName].filter(Boolean).join(" - ") || posName || "Unnamed";
        };

        const result: Record<number, any> = {};
        filtered.forEach((row) => {
          result[row["PositionId"]] = {
            id: row["PositionId"],
            name: formatNodeName(row),
            parent: row["ReportingTo"],
            children: [],
          };
        });

        // Ensure placeholder root for broken links: prefer id 0, fallback to -1
        const PLACEHOLDER_ID: number = result[0] ? -1 : 0;
        if (!result[PLACEHOLDER_ID]) {
          result[PLACEHOLDER_ID] = {
            id: PLACEHOLDER_ID,
            name: "Unassigned",
            parent: null,
            children: [],
          };
        } else {
          result[PLACEHOLDER_ID].parent = null;
          result[PLACEHOLDER_ID].name =
            result[PLACEHOLDER_ID].name || "Unassigned";
          result[PLACEHOLDER_ID].children =
            result[PLACEHOLDER_ID].children || [];
        }

        // Helper to detect cycles
        const createsCycle = (
          currentId: number,
          parentId: number | null
        ): boolean => {
          if (parentId == null) return false;
          if (parentId === currentId) return true;
          const seen = new Set<number>([currentId]);
          let cur: number | null | undefined = parentId;
          while (cur != null) {
            if (seen.has(cur)) return true;
            seen.add(cur);
            const next: number | null | undefined = result[cur]?.parent as
              | number
              | null
              | undefined;
            if (next != null && result[next] === undefined) return false; // broken chain ends
            cur = next ?? null;
          }
          return false;
        };

        // Sanitize parents: missing/self-parent/cycles -> attach to placeholder
        Object.values(result).forEach((node) => {
          if (!node) return;
          if (node.id === PLACEHOLDER_ID) return;
          let p: number | null = node.parent as number | null;
          const missing = p != null && result[p] === undefined;
          const selfParent = p === node.id;
          const hasCycle = createsCycle(node.id, p);
          if (missing || selfParent || hasCycle || p == null) {
            p = PLACEHOLDER_ID;
          }
          node.parent = p;
        });

        // Rebuild children arrays
        Object.values(result).forEach((n) => {
          if (n) n.children = [];
        });
        Object.values(result).forEach((node) => {
          if (!node) return;
          const p: number | null = node.parent as number | null;
          if (p != null && result[p]) {
            result[p].children.push(node.id);
          }
        });

        const unlinkedPositions = filtered.filter(
          (row) =>
            !result[row.PositionId].parent &&
            result[row.PositionId].children.length === 0
        );
        // console.log('❌ Unlinked Positions (No Parent, No Children):', unlinkedPositions);

        // Filter out unlinked positions without children from the tree data
        const treeData: INode<any>[] = Object.values(result).filter((node) => {
          const isUnlinked = node.parent === null && node.children.length === 0;
          if (isUnlinked) {
            console.log("❌ Excluding Unlinked Position:", node);
          }
          return !isUnlinked;
        });
        setTreeData(treeData);
      } catch (error) {
        console.error(
          `❌ Failed to load or parse schema/data for ${tableName}`,
          error
        );
      }
    };

    loadPositionData();
  }, [unitId]);

  const getAllParentIds = (data: any[]) => {
    return data
      .filter((node) => node.children && node.children.length > 0)
      .map((node) => node.id);
  };
  const parentIds = getAllParentIds(treeData);

  return (
    <>
      <div
        className="light-box bg-primary"
        style={{
          cursor: "pointer",
          borderRadius: "5px",
          padding: "7px 10px",
        }}
        onClick={toggle}
      >
        <Filter size={18} />

        {selectedIds.length > 0 &&
          (() => {
            const names = selectedIds
              .map((id) => {
                const found = rawData.find((row) => row.PositionId === id);
                return found ? getDisplayName(found) : id;
              })
              .join(", ");

            const shortText =
              names.slice(0, 30) + (names.length > 30 ? "..." : "");

            return (
              <span
                style={{ marginLeft: "10px", color: "white" }}
                title={names} // 👈 full tooltip text here
              >
                {selectedIds.length} : {shortText}
              </span>
            );
          })()}
      </div>

      <CommonModal
        isOpen={modal}
        toggle={toggle}
        saveText="Apply"
        title="Position Tree"
        onSave={() => {
          const selectedData = rawData.filter((row) =>
            selectedIds.includes(row.PositionId)
          );

          // Dispatch selected positions to Redux
          // dispatch(
          //   setPositions(
          //     selectedData.map((row) => ({
          //       id: row.PositionId,
          //       name: row.PositionName,
          //     }))
          //   )
          // );

          setModal(!modal);
          if (onSelectionChange) onSelectionChange(selectedIds);
        }}
      >
        <Col sm={12}>
          <CardBody>
            {treeData.length > 0 ? (
              <TreeView
                data={treeData}
                aria-label="Checkbox tree"
                multiSelect
                propagateSelect
                propagateSelectUpwards
                togglableSelect
                selectedIds={selectedIds} // <-- controlled value
                defaultExpandedIds={parentIds}
                onSelect={({ treeState }) => {
                  setSelectedIds(Array.from(treeState.selectedIds));
                }}
                nodeRenderer={({
                  element,
                  isBranch,
                  isExpanded,
                  isSelected,
                  isHalfSelected,
                  getNodeProps,
                  level,
                  handleSelect,
                  handleExpand,
                }) => (
                  <div
                    {...getNodeProps({ onClick: handleExpand })}
                    style={{ marginLeft: 40 * (level - 1) }}
                  >
                    {isBranch && <ArrowIcon isOpen={isExpanded} />}
                    <CheckBoxIcon
                      className="checkbox-icon"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        handleSelect(e);
                        e.stopPropagation();
                      }}
                      variant={
                        isHalfSelected ? "some" : isSelected ? "all" : "none"
                      }
                    />
                    <span className="name ms-2">
                      {element?.name || "Unnamed"}
                    </span>
                  </div>
                )}
              />
            ) : (
              <div>Loading tree...</div>
            )}
          </CardBody>
        </Col>
      </CommonModal>
    </>
  );
}
